import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Aside from "../../../components/Aside";
import CustomDataTable from "../../../components/CustomDataTable";
import Footer from "../../../components/Footer";
import Header from "../../../components/Header";
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import { DateUtil } from "../../../utils/date";
import { NumberUtil } from "../../../utils/number";
import { ReportService } from "../../../services/ReportsService";
import { AppContext } from "../../../context/AppContext";
import { useTranslation } from "react-i18next";
import * as XLSX from 'xlsx';
import useRequest from "../../../hooks/useRequest";
import LoaderSmall from "../../../components/LoaderSmall";
import usePermission from "../../../hooks/usePermission";

function CustomersReportPage() {

    const { t } = useTranslation();
    const { call, isRequesting} = useRequest();
    const { setShowCustomerDetail } = useContext(AppContext);
    const hasActionsAccess = usePermission('reports.actions');
    const hasDownloadAccess = usePermission('reports.download');
    const { account } = useCurrentAccount();

    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [targets, setTargets] = useState({
        new_clients: 0,
        recurrence_clients: 0,
        retention_clients: 0,
        total_clients: 0,
        total_clients_in_period: 0,
    })
    const [filterRangeDates, setFilterRangeDates] = useState({ start: null, end: null })

    const columns = [
        {
            name: t("reports.customers.name"),
            sortable: true,
            selector: row => row.name,
            cell: row => {
                return <p className="text-nowrap mb-0">{row.name}</p>
            },
        },
        {
            name: t("reports.customers.number"),
            sortable: true,
            center: true,
            selector: row => row.number,
            cell: row => {
                return <p className="text-nowrap mb-0">{row.number}</p>
            },
        },
        {
            name: t("reports.customers.blocked-by"),
            sortable: true,
            center: true,
            selector: row => row.blockedBy,
            cell: row => {
                return( 
                    <>
                    {
                        row.blockedBy === 'N/A' ? 
                        <span className="badge bg-soft-info text-info fs-6">
                        <i className="bi bi-exclamation-circle"></i> {t('empty')}
                    </span> 
                    :
                    <div className="text-left">
                    <span className="badge bg-soft-primary text-primary fs-6">
                        <i className="bi bi-person-circle"></i>  {row.blockedBy}
                    </span>
                </div>
                    }
                    </>
            );
            },
        },
        {
            name: t("reports.customers.date-blocking"),
            sortable: true,
            center: true,
            selector: row => DateUtil.format(row.dateBlock),
            cell: row => {
                return (
                    <>
                    {
                        row.dateBlock === 'N/A' ? 
                        <span className="badge bg-soft-info text-info fs-6">
                        <i className="bi bi-exclamation-circle"></i> {t('empty')}
                    </span>:
                    <p className="text-nowrap mb-0">{DateUtil.format(row.dateBlock)}</p>
                    }
                    </>
                );
            }
        },

        {
            name: t("reports.customers.date-registration"),
            sortable: true,
            center: true,
            selector: row => DateUtil.format(row.date_register),
            cell: row => <p className="text-nowrap mb-0">{DateUtil.format(row.date_register)}</p>,
        },
        {
            name: t("reports.customers.last-contact"),
            sortable: true,
            center: true,
            selector: row => DateUtil.format(row.date_last_contact),
            cell: row => <p className="text-nowrap mb-0">{DateUtil.format(row.date_last_contact)}</p>,
        },
        {
            name: t("reports.customers.last-closure"),
            sortable: true,
            center: true,
            selector: row => row.last_tipification_resolve,
            cell: row => {
                return <p className="text-nowrap mb-0">{row.last_tipification_resolve}</p>
            },
        },
        {
            name: t("reports.customers.total-tickets"),
            sortable: true,
            center: true,
            selector: row => row.total_tickets_in_period,
            cell: row => {
                return <p className="text-nowrap mb-0">{row.total_tickets_in_period}</p>
            },
        },
        {
            name: t('status'),
            sortable: true,
            formatToExport: row => row.status_number === 'Activo' ? t('reports.customers.active'): t('reports.customers.blocked'),
            selector: row => row.status_number,
            cell: (row) => {
                return (
                    row.status_number === 'Activo' ?
                    (
                        <span className="badge bg-soft-info text-info">
                            <span className="legend-indicator bg-info"></span> {t('reports.customers.active')}
                        </span>
                    ) : 
                    (
                        <span className="badge bg-soft-danger text-danger">
                            <span className="legend-indicator bg-danger"></span> {t('reports.customers.blocked')}
                        </span>
                    )
                )
            }

        },
        ...(hasActionsAccess ? [{
            name: t("reports.customers.actions"),
            sortable: true,
            exportable: false,
            cell: (row) => {
                return (
                    <div className="btn-group" role="group">
                        <button className="btn btn-white btn-sm" onClick={() => setShowCustomerDetail({ account_client_id: row.accounts_client_id, show: true })} >
                            <i className="bi-pencil-fill me-2"></i> {t("btn-edit")}
                        </button>
                    </div>
                )
            }
        }] : []),
    ];

    const getReport = async() => {
        if (filterRangeDates.start && filterRangeDates.end) {
            const start = filterRangeDates.start.format('YYYY-MM-DD')
            const end = filterRangeDates.end.format('YYYY-MM-DD')
            const { data } = await call(() => ReportService.getReportCustomers(account.id, start, end));

            setTargets(data.tarjets)
            setRows(data.rows)
        }
    }

    const handleRowsFiltered = useCallback((filteredRows: any) => {
        setFilteredRows(prevRows => {
            if (JSON.stringify(prevRows) !== JSON.stringify(filteredRows)) {
                return filteredRows;
            }
            return prevRows;
        });
    }, []);

    const downloadReport = (isCSV = false) => {
        const HEADER = columns
            .filter(c => c.exportable !== false)
            .map(c => c.name);

        const data = filteredRows.map(row => {
            const formattedRow = {};
            columns.forEach(col => {
                if (col.exportable !== false) {
                    formattedRow[col.name] = col.formatToExport
                        ? col.formatToExport(row)
                        : col.selector(row) || '-';
                }
            });
            return formattedRow;
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data, { header: HEADER });

        if (isCSV) {
            const csv = XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'filtered_report.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            XLSX.utils.book_append_sheet(wb, ws);
            XLSX.writeFile(wb, 'filtered_report.xlsx');
        }
    };

    useEffect(() => {
      getReport()
    }, [filterRangeDates])

    const heightRequesting = useMemo(()=> {return isRequesting ? 'mt-50 mb-50':''},[isRequesting]);
    return (
        <>
            <Header />
            <Aside />

            <main id="content" role="main" className="main">

                <div className="content container-fluid">

                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t("reports.operation-title")}</span></li>
                                        <li className="breadcrumb-item " aria-current="page">{t("reports.reports-title")}</li>
                                        <li className="breadcrumb-item active" aria-current="page">{t("reports.customers.customers-title")}</li>
                                    </ol>
                                </nav>
                                <h1 className="page-header-title"><i className="bi-grid-1x2 nav-icon"></i> {t("reports.reports-title")} - {t("reports.customers.customers-title")}</h1>
                            </div>

                        </div>
                    </div>

                    <div className=" mb-3 mb-lg-5">


                        <div className={`row gx-lg-6 relative ${heightRequesting}`}>
                            {
                                isRequesting ? <LoaderSmall/> :
                            <>
                            <div className="col-lg-3 mb-md-3 mb-3">
                                <div className="card card-sm " >
                                    <div className="card-body">
                                        <div className="d-flex ">
                                            <div className="flex-grow-1 ">
                                                <h6 className="card-subtitle mb-3">{t("reports.customers.period-customers")}</h6>
                                                <div className="">
                                                    <h1 className="card-title">{NumberUtil.format(targets.total_clients_in_period)} <span className="text-body fs-5 ms-1">{t("reports.customers.of")} {NumberUtil.format(targets.total_clients)} {t("reports.customers.total")}</span> </h1>
                                                    
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 mb-md-3 mb-3">
                                <div className="card card-sm " >
                                    <div className="card-body">
                                        <div className="d-flex ">
                                            <div className="flex-grow-1 ">
                                                <h6 className="card-subtitle mb-3">{t("reports.customers.new-customers")}</h6>
                                                <div className="">
                                                    <h1 className="card-title">{NumberUtil.format(targets.new_clients)}</h1>
                                                    {/* <span className="text-body fs-5 ms-1">{NumberUtil.format(totalApproved)} ordenes de aprobadas</span> */}
                                                </div>


                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 mb-md-3 mb-3">
                                <div className="card card-sm ">
                                    <div className="card-body">
                                        <div className="d-flex">
                                            <div className="flex-grow-1">
                                                <h6 className="card-subtitle mb-3">{t("reports.customers.retention-customers")}</h6>
                                                <div className="">
                                                    <h1 className="card-title">{NumberUtil.format(targets.retention_clients)}%</h1>
                                                    {/* <span className="text-body fs-5 ms-1">{NumberUtil.format(totalPending)} ordenes en proceso</span> */}
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3">
                                <div className="card card-sm ">
                                    <div className="card-body">
                                        <div className="d-flex">
                                            <div className="flex-grow-1">
                                                <h6 className="card-subtitle mb-3">{t("reports.customers.recurring-customers")}</h6>
                                                <div className="">
                                                    <h1 className="card-title">{NumberUtil.format(targets.recurrence_clients)}%</h1>
                                                    {/* <span className="text-body fs-5 ms-1">{NumberUtil.format(totalFailed)} ordenes fallidas</span> */}
                                                </div>

                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                            </>
                            }
                        </div>

                    </div>


                    <CustomDataTable
                        columns={columns}
                        showDates
                        data={rows}
                        hideAccountsFilter
                        filterDay={0}
                        onRowsFiltered={handleRowsFiltered}
                        isLoading={isRequesting}
                        onExport={hasDownloadAccess ? (format) => downloadReport(format === 'csv') : null}
                        onDatesChange={(range) => setFilterRangeDates(range)}
                        filter={(row, term) => {
                            if (!term) return true;

                            return (
                                `${row.ticket_id}`.match(new RegExp(`${term}`, 'i'))  ||
                                `${row.last_tipification_resolve}`.match(new RegExp(`${term}`, 'i'))  ||
                                `${row.name}`.match(new RegExp(`${term}`, 'i'))  ||
                                `${row.number}`.match(new RegExp(`${term}`, 'i'))  ||
                                `${row.status_number}`.match(new RegExp(`${term}`, 'i')) ||
                                `${row.blockedBy}`.match(new RegExp(`${term}`, 'i'))
                            )
                        }}
                    />

                </div>
                <Footer />
            </main>


        </>
    )
}
export default CustomersReportPage